import { Button, Center, EmptyState, Table, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconMailbox, IconPlus } from '@tabler/icons-react';
import { Panel } from 'components/Panel';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import qs from 'query-string';
import { useEffect } from 'react';
import { useLoadExternalMailboxConfigurations } from './api';
import { ExternalMailboxForm } from './ExternalMailboxForm';
import { ExternalMailboxRow } from './ExternalMailboxRow';

export const ExternalMailboxCard = () => {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();

  const { externalMailboxConfigurations, isExternalMailboxConfigurationsInitialLoading } =
    useLoadExternalMailboxConfigurations();

  const canAddMailbox = company.package.can_add_mailbox;
  const mailboxes = externalMailboxConfigurations.data;

  // The Microsoft OAuth callback redirects back to this page with the outcome of the
  // token exchange in the query string.
  const { message } = qs.parse(location.search);

  useEffect(() => {
    if (typeof message !== 'string') return;

    notifications.show({ title: t(i18nKeys.SUCCESS), color: 'green', message });
  }, [message, t]);

  const openCreateModal = () =>
    modals.open({
      title: t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.ADD),
      centered: true,
      size: 'lg',
      children: <ExternalMailboxForm onClose={modals.closeAll} />,
    });

  const addButton = (
    <Button
      variant="default"
      size="compact-sm"
      leftSection={<IconPlus size={15} stroke={1.8} />}
      disabled={!canAddMailbox}
      onClick={openCreateModal}
    >
      {t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.ADD)}
    </Button>
  );

  return (
    <Panel
      withBorder
      flushContent
      title={t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.CARD_TITLE)}
      isLoading={isExternalMailboxConfigurationsInitialLoading}
      topRight={
        canAddMailbox ? (
          addButton
        ) : (
          <Tooltip label={t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.LIMIT_EXCEEDED)}>
            <span>{addButton}</span>
          </Tooltip>
        )
      }
    >
      {mailboxes.length === 0 ? (
        <Center py="xl" px="md">
          <EmptyState
            icon={<IconMailbox size={48} stroke={1.5} />}
            title={t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.EMPTY_STATE.TITLE)}
            description={t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.EMPTY_STATE.DESCRIPTION)}
          />
        </Center>
      ) : (
        <Table verticalSpacing="sm" horizontalSpacing="lg" highlightOnHover>
          <Table.Tbody>
            {mailboxes.map(mailbox => (
              <ExternalMailboxRow key={mailbox.id} mailbox={mailbox} />
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Panel>
  );
};
